import {
	autoUpdate,
	computePosition,
	flip,
	offset,
	shift,
} from '@floating-ui/dom';
import type { Editor, Range } from '@tiptap/core';
import { Extension } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import type { ReactNode, RefObject } from 'react';
import { EditorCommandOut } from '../components/editor-command';

type GetRect = () => DOMRect;

async function positionFloating(getRect: GetRect, floating: HTMLElement) {
	const virtualEl = { getBoundingClientRect: getRect };
	const { x, y } = await computePosition(virtualEl as Element, floating, {
		placement: 'bottom-start',
		middleware: [offset(6), flip(), shift({ padding: 8 })],
	});
	Object.assign(floating.style, { left: `${x}px`, top: `${y}px` });
}

export let rectOverride: GetRect | null = null;

export function setRectOverride( fn: GetRect | null ): void {
	rectOverride = fn;
}

const Command = Extension.create({
	name: 'slash-command',
	addOptions() {
		return {
			suggestion: {
				char: '/',
				command: ({ editor, range, props }) => {
					props.command({ editor, range });
				},
			} as SuggestionOptions,
		};
	},
	addProseMirrorPlugins() {
		return [
			Suggestion({
				// @ts-ignore
				editor: this.editor,
				...this.options.suggestion,
			}),
		];
	},
});

const renderItems = (elementRef?: RefObject<Element> | null) => {
	let component: ReactRenderer | null = null;
	let stopAutoUpdate: (() => void) | null = null;

	const destroy = () => {
		stopAutoUpdate?.();
		component?.element?.remove();
		component?.destroy();
		component = null;
		stopAutoUpdate = null;
	};

	let getCurrentRect: GetRect = () => new DOMRect();

	return {
		onStart: (props: {
			editor: Editor;
			clientRect?: (() => DOMRect | null) | null;
		}) => {
			const { selection } = props.editor.state;
			const parentNode = selection.$from.node(selection.$from.depth);
			if (parentNode.type.name === 'codeBlock') return;

			component = new ReactRenderer(EditorCommandOut, {
				props,
				editor: props.editor,
			});

			const container = elementRef?.current ?? document.body;
			container.appendChild(component.element);
			const floating = component.element as HTMLElement;
			Object.assign(floating.style, { position: 'fixed', zIndex: '9999' });
			getCurrentRect =
				rectOverride ?? (() => props.clientRect?.() ?? new DOMRect());
			rectOverride = null;
			const reference = {
				getBoundingClientRect: () => getCurrentRect(),
				contextElement: props.editor.view.dom,
			};

			stopAutoUpdate = autoUpdate(reference, floating, () =>
				positionFloating(() => getCurrentRect(), floating),
			);
		},

		onUpdate: (props: {
			editor: Editor;
			clientRect?: (() => DOMRect | null) | null;
		}) => {
			if (!component) return;
			component.updateProps(props);
			getCurrentRect = () => props.clientRect?.() ?? new DOMRect();
			positionFloating(getCurrentRect, component.element as HTMLElement);
		},

		onKeyDown: (props: { event: KeyboardEvent }) => {
			if (props.event.key === 'Escape') {
				destroy();
				return true;
			}
			return (component?.ref as any)?.onKeyDown?.(props.event) ?? false;
		},

		onExit: () => {
			destroy();
		},
	};
};

export interface SuggestionItem {
	title: string;
	description: string;
	icon: ReactNode;
	searchTerms?: string[];
	command?: (props: { editor: Editor; range: Range }) => void;
	group?: string;
}

export const createSuggestionItems = (items: SuggestionItem[]) => items;

export const handleCommandNavigation = (event: KeyboardEvent) => {
	if (['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
		const slashCommand = document.querySelector('#slash-command');
		if (slashCommand) return true;
	}
};

export { Command, renderItems };
